import { Injectable, OnDestroy } from '@angular/core';

const CHECK_INTERVAL = 15000; // in ms
const RESET_INTERVAL = 1000; // Throttle synchronous storage writes for rapid-fire events.
const STORE_KEY = 'lastAction';
@Injectable({ providedIn: 'root' })
export class AutoLogoutService implements OnDestroy {
  private getLastAction() {
    const storedValue = localStorage.getItem(STORE_KEY);
    return storedValue === null ? NaN : Number(storedValue);
  }

  private setLastAction(lastAction: number) {
    this._lastAction = lastAction;
    localStorage.setItem(STORE_KEY, lastAction.toString());
  }

  // local-storage syncs activity across all tabs, but throttle on a local copy to prevent excessive writes from rapid-fire events.
  private _lastAction = 0;
  private readonly onActivity = () => {
    const now = Date.now();
    if (now - this._lastAction >= RESET_INTERVAL) {
      this.setLastAction(now);
    }
  };

  private initListener() {
    // Capture observes activity even when a component stops propagation; also scroll requires it since it does not bubble.
    document.addEventListener('pointerdown', this.onActivity, { capture: true, passive: true });
    document.addEventListener('keydown', this.onActivity, { capture: true, passive: true });
    document.addEventListener('scroll', this.onActivity, { capture: true, passive: true });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private removeListener() {
    document.removeEventListener('pointerdown', this.onActivity, true);
    document.removeEventListener('keydown', this.onActivity, true);
    document.removeEventListener('scroll', this.onActivity, true);
  }

  private _timer: ReturnType<typeof setInterval> | null = null;
  private _timeoutInMinutes = 0;
  private _onTimeout!: () => void;

  start(timeoutInMinutes: number, onTimeout: () => void) {
    if (!Number.isFinite(timeoutInMinutes) || timeoutInMinutes <= 0) {
      this._timeoutInMinutes = 0;
      this.stop();
      return;
    }

    this._timeoutInMinutes = timeoutInMinutes;
    this._onTimeout = onTimeout;

    if (this._timer !== null) return;

    this.setLastAction(Date.now());
    this.initListener();
    this._timer = setInterval(() => {
      this.check();
    }, CHECK_INTERVAL);
  }

  stop() {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }

    this.removeListener();
  }

  private check() {
    if (this._timeoutInMinutes === 0) return;

    const now = Date.now();
    const lastAction = this.getLastAction();
    // Protection against corrupt or missing lastAction value to avoid unexpected forced logout:
    if (!Number.isFinite(lastAction) || lastAction <= 0 || lastAction > now) {
      this.setLastAction(now);
      return;
    }

    const timeoutInMs = this._timeoutInMinutes * 60 * 1000;
    const timeoutTime = lastAction + timeoutInMs;

    if (now > timeoutTime) {
      this.stop();
      this._onTimeout();
    }
  }
}
